import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { queryFilteredDemandSources } from '~/src/dash/api/filters/demandSources';
import {
  fetchDeals,
  fetchSellerPublishers,
  fetchUniqueDemandOpportunities,
} from '~/src/dash/apiV2/platform-connect/platform-connect';
import { ICustomRenderCellJSXProps } from '~/src/dash/components/tables/ListViewTable/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { extractRouteParams, navigate, RoutePlatformConnects } from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  IDealIDAndName,
  IDemandSourcesIDAndName,
  IGetDealExtSellerPublishersResponse,
  IGetUniqueDemandOpportunitiesResponse,
  IPlatformConnectionDealExtRow,
  IPlatformConnectionUniqueDemandRow,
  IPlatformConnectListFilters,
  IPlatformConnectSelectedValue,
  IPlatformConnectUiParams,
} from '~/src/dash/views/platform-connect/common';
import { formatParams } from '~/src/dash/views/platform-connect/common';
import { PlatformConnectDraftOfferNewView } from '~/src/dash/views/platform-connect/DraftOfferNew';
import { IOfferNewConfig } from '~/src/dash/views/platform-connect/DraftOfferNew/config';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const PlatformConnectOfferNewContainer = () => {
  const { session, user } = useBaseStore();
  const { PlatformConnect } = useBaseConfig();
  const cfg = PlatformConnect.DraftOfferNew as IOfferNewConfig;

  const [filters, setFilters] = useState<IPlatformConnectListFilters>({});
  const [selectedValues, setSelectedValues] = useState<IPlatformConnectSelectedValue>({});

  const [uniqueDemandOpportunitiesRows, setUniqueDemandOpportunitiesRows] = useState<
    IPlatformConnectionUniqueDemandRow[]
  >([]);
  const [uniqueDemandTotalRows, setUniqueDemandTotalRows] = useState(0);

  const [dealExtensionOpportunitiesRows, setDealExtensionOpportunitiesRows] = useState<
    IPlatformConnectionDealExtRow[]
  >([]);
  const [dealExtensionTotalRows, setDealExtensionTotalRows] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [error, setError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dealExtLoadState, setDealExtLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [uniqueDemandLoadState, setUniqueDemandLoadState] =
    useState<LoadingState>(LoadingStateLoading);

  const [activeRowInfo, setActiveRowInfo] = useState({});
  const [deal, setDeal] = useState<IDealIDAndName>({});
  const [disableDealExtOpportunities, setDisableDealExtOpportunities] = useState(true);
  const [disableUniqueDemandOpportunities, setDisableUniqueDemandOpportunities] = useState(true);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (selectedValues.deal && selectedValues.impressionsGoal) {
      setDisableDealExtOpportunities(false);
    }
  }, [selectedValues]);

  // removes Direct Campaign, Fixed Price Platform Connection, Dynamic Platform Connection, and Other (VAST tag)
  const getFilteredDemandSourcesForPlatformConnectList = (
    session: ISessionState,
    user: IUserState,
    siteIDs?: string, // example: '30, 11, 23'
  ): Promise<Record<string, IMenuList>> => {
    const publisherID = session.publisher.ID;
    return queryFilteredDemandSources(
      publisherID,
      {
        publisher_id: publisherID,
        site_ids: siteIDs || '',
      },
      true,
    )
      .then(({ DemandSourcesMap }) => {
        const demandSourceAdapters = [
          ...getHeaderBidderAdaptersForPublisher(
            '',
            false,
            session.publisher,
            hasRoleSuperUser(user.user),
          ),
        ];
        const demandSourcesList = {} as Record<string, IListItem>;
        demandSourceAdapters.forEach((adapter) => {
          if (
            DemandSourcesMap[adapter.key] &&
            ![
              'publicaCampaign',
              'platformConnection',
              'dynamicPlatformConnection',
              'publica',
            ].includes(adapter.key)
          ) {
            demandSourcesList[adapter.key] = {
              ID: adapter.key,
              title: adapter.text,
              selected: false,
            };
          }
        });
        return { 'Filtered Demand Sources': demandSourcesList };
      })
      .catch((err) => {
        console.error(err);
        return { 'Filtered Demand Sources': {} };
      });
  };

  const onChange = (key: string, value: string | number | IMenuList) => {
    if (key === 'deal') {
      const newVal = parseInt(value as string, 10) || 0;
      const clonedSelectedValues = cloneDeep(selectedValues);
      clonedSelectedValues[key] = newVal;
      setSelectedValues(clonedSelectedValues);
      for (const currDeal of filters[key] as ISingleSelectOption[]) {
        if (currDeal.value === (value as string)) {
          setDeal({ ID: currDeal.value, Name: currDeal.label });
        }
      }
    } else if (key === 'impressionsGoal') {
      const newVal = parseInt(value as string, 10) || 0;
      const clonedSelectedValues = cloneDeep(selectedValues);
      clonedSelectedValues[key] = newVal;
      setSelectedValues(clonedSelectedValues);
    } else if (key === 'demandSource') {
      const clonedFilters = cloneDeep(filters);
      clonedFilters[key] = value as IMenuList;
      unstable_batchedUpdates(() => {
        setFilters(clonedFilters);
        setDisableUniqueDemandOpportunities(false);
      });
    }
  };

  const onAcceptDemandSource = (key: string): void => {
    const demandSources: IDemandSourcesIDAndName[] = [];
    Object.values(filters[key]).forEach((listItem: IListItem) => {
      if (listItem.selected) {
        demandSources.push({ ID: listItem.ID, Name: listItem.title });
      }
    });
    setSelectedValues((prev) => Object.assign(prev, { [key]: demandSources }));
  };

  const handleClickOpenDrawer = (props: ICustomRenderCellJSXProps) => {
    unstable_batchedUpdates(() => {
      setIsModalOpen(true);
      setActiveRowInfo(props.row);
    });
  };

  const onFetchPlatformConnectSellerPublishers = (params: IPlatformConnectUiParams) => {
    setDealExtLoadState(LoadingStateLoading);
    fetchSellerPublishers(
      session.publisher.ID,
      String(selectedValues?.deal as number),
      formatParams(params),
    )
      .then((resp: IGetDealExtSellerPublishersResponse) => {
        const rows = resp.DealExtSellerPublishers.map((seller) => {
          return {
            ID: parseInt(seller.SellerPublisher.ID, 10),
            Name: seller.SellerPublisher.name,
            Status: seller.Status,
          } as IPlatformConnectionDealExtRow;
        });
        unstable_batchedUpdates(() => {
          setDealExtensionOpportunitiesRows(rows || []);
          setDealExtensionTotalRows(resp.TotalRows);
          setDealExtLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error retrieving data.');
          setDealExtLoadState(LoadingStateNoData);
        });
      });
  };

  const onfetchUniqueDemandOpportunities = (params: IPlatformConnectUiParams) => {
    setUniqueDemandLoadState(LoadingStateLoading);
    fetchUniqueDemandOpportunities(
      session.publisher.ID,
      selectedValues.demandSource as IDemandSourcesIDAndName[],
      formatParams(params),
    )
      .then(({ UniqueDemandOpportunities, TotalRows }: IGetUniqueDemandOpportunitiesResponse) => {
        const rows = UniqueDemandOpportunities.map((uniqueDemandOpportunity) => {
          return {
            ID: uniqueDemandOpportunity.SellerPublisher.ID as unknown as number,
            Name: uniqueDemandOpportunity.SellerPublisher.name,
            DemandMatch: uniqueDemandOpportunity.DemandMatch,
          } as IPlatformConnectionUniqueDemandRow;
        });
        unstable_batchedUpdates(() => {
          setUniqueDemandOpportunitiesRows(rows || []);
          setUniqueDemandTotalRows(TotalRows);
          setUniqueDemandLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setError(true);
          setSnackbarMessage('Error retrieving data.');
          setUniqueDemandLoadState(LoadingStateNoData);
        });
      });
  };

  // main query
  const getData = () => {
    if (true) {
      unstable_batchedUpdates(() => {
        setDealExtLoadState(LoadingStateLoading);
        setUniqueDemandLoadState(LoadingStateLoading);
      });
      Promise.all([
        // deal extension
        fetchDeals(session.publisher.ID),
        // unique demand
        getFilteredDemandSourcesForPlatformConnectList(session, user),
      ])
        .then((resp: any) => {
          const [deals, demandSources] = resp;

          const dealSingleSelectOptions = [] as ISingleSelectOption[];
          for (const deal of deals) {
            dealSingleSelectOptions.push({
              label: deal.Name,
              value: deal.ID.toString(),
            });
          }

          setFilters((prev) =>
            Object.assign(prev, {
              deal: dealSingleSelectOptions,
              demandSource: demandSources['Filtered Demand Sources'],
            }),
          );

          unstable_batchedUpdates(() => {
            setDealExtLoadState(LoadingStateHasData);
            setUniqueDemandLoadState(LoadingStateHasData);
          });
        })
        .catch(() => {
          unstable_batchedUpdates(() => {
            setFilters({ deals: [], demandSource: {} });
            setDealExtLoadState(LoadingStateNoData);
            setUniqueDemandLoadState(LoadingStateNoData);
            setSnackbarMessage('Error retrieving data.');
            setError(true);
          });
        });
    }
  };

  // route to return to platform connect home page
  const navigateToPlatformConnectsView = () => {
    navigate(RoutePlatformConnects, extractRouteParams({ session, user }));
  };

  return (
    <>
      <PlatformConnectDraftOfferNewView
        filters={filters}
        selectedValues={selectedValues}
        fetchSellerPublishers={onFetchPlatformConnectSellerPublishers}
        fetchUniqueDemandOpportunities={onfetchUniqueDemandOpportunities}
        cfg={cfg}
        navigateToPlatformConnectsView={navigateToPlatformConnectsView}
        dealExtensionOpportunitiesRows={dealExtensionOpportunitiesRows}
        setDealExtensionOpportunitiesRows={setDealExtensionOpportunitiesRows}
        uniqueDemandOpportunitiesRows={uniqueDemandOpportunitiesRows}
        dealExtensionTotalRows={dealExtensionTotalRows}
        uniqueDemandTotalRows={uniqueDemandTotalRows}
        dealExtLoadState={dealExtLoadState}
        uniqueDemandLoadState={uniqueDemandLoadState}
        onChange={onChange}
        onAcceptDemandSource={onAcceptDemandSource}
        handleClickOpenDrawer={handleClickOpenDrawer}
        activeRowInfo={activeRowInfo}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        error={error}
        setError={setError}
        setSnackbarMessage={setSnackbarMessage}
        deal={deal}
        disableDealExtOpportunities={disableDealExtOpportunities}
        disableUniqueDemandOpportunities={disableUniqueDemandOpportunities}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
